import { Fragment, type CSSProperties } from "react";
import { styled } from "@pigment-css/react";
import type {
  OrderCard as OrderCardSchema,
  OrderCardSection,
  OrderInvoiceStatus as CardInvoiceStatus,
  OrderListSection,
  OrderRhythmSection,
} from "@acto/genui-components-schema";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { OrderInvoiceStatus } from "@acto/gql/graphql";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { Card, MetaLabel, SubLabel } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { OrderRows } from "@acto/taskpane/field-sales-2/components/patterns/OrderRows";
import { formatCurrency } from "@acto/taskpane/field-sales-2/utils/format";
import { CardHeader } from "./CardHeader";
import { KpiPair } from "./KpiPair";
import { OrderScopeEntries, useOrderScopeLabel } from "./OrderScopeEntries";
import { useOrderDrill } from "./useOrderDrill";

const INVOICE_STATUS: Record<CardInvoiceStatus, OrderInvoiceStatus> = {
  open: OrderInvoiceStatus.Open,
  partiallyInvoiced: OrderInvoiceStatus.PartiallyInvoiced,
  invoiced: OrderInvoiceStatus.Invoiced,
};

const Summary = styled("p")(({ theme }) => ({
  margin: 0,
  paddingTop: theme.fieldSalesTwo.spacing[8],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[400],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.wide,
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const Rhythm = styled("dl")(({ theme }) => ({
  margin: 0,
  display: "grid",
  gridTemplateColumns: `${theme.fieldSalesTwo.spacing[50]} 1fr`,
  alignItems: "center",
  columnGap: theme.fieldSalesTwo.spacing[12],
  rowGap: theme.fieldSalesTwo.spacing[16],
}));

const RhythmTrack = styled("dd")(({ theme }) => ({
  margin: 0,
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[12],
  minWidth: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[900],
  whiteSpace: "nowrap",
}));

const RhythmLabel = styled(SubLabel)({ lineHeight: "normal" });

const RHYTHM_VALUE_ROOM = "80";

const RhythmBar = styled("span")<{ overdue: boolean }>(({ theme }) => ({
  flexShrink: 1,
  width: `calc((100% - ${theme.fieldSalesTwo.spacing[RHYTHM_VALUE_ROOM]}) * var(--share-of-longest))`,
  minWidth: theme.fieldSalesTwo.spacing[16],
  height: theme.fieldSalesTwo.spacing[12],
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.neutral[800],
  variants: [{ props: { overdue: true }, style: { background: theme.fieldSalesTwo.colors.charts.primary } }],
}));

const RhythmValue = styled("span")<{ overdue: boolean }>(({ theme }) => ({
  flexShrink: 0,
  variants: [{ props: { overdue: true }, style: { color: theme.fieldSalesTwo.colors.charts.primary } }],
}));

const OrderList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
}));

interface OrderCardProps {
  part: OrderCardSchema;
}

export function OrderCard({ part }: OrderCardProps) {
  return (
    <Card nested>
      <CardHeader label={part.label} headline={part.headline}>
        {part.summary && <Summary>{part.summary}</Summary>}
      </CardHeader>
      {part.sections.map((section, index) => (
        <Fragment key={index}>
          <Divider />
          <Section section={section} orderListEyebrow={part.label?.text} />
        </Fragment>
      ))}
    </Card>
  );
}

function Section({ section, orderListEyebrow }: { section: OrderCardSection; orderListEyebrow?: string }) {
  switch (section.kind) {
    case "rhythm":
      return <RhythmSection section={section} />;
    case "kpis":
      return <KpiPair items={section.items} />;
    case "orders":
      return <OrdersSection section={section} orderListEyebrow={orderListEyebrow} />;
    default: {
      section satisfies never;
      return null;
    }
  }
}

function RhythmSection({ section }: { section: OrderRhythmSection }) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.orderCard");
  const latest =
    "daysSinceLastOrder" in section
      ? { label: t("sinceLastOrderLabel"), days: section.daysSinceLastOrder }
      : { label: t("currentLabel"), days: section.currentCycleDays };
  const longest = Math.max(section.normalCycleDays, latest.days);
  const isOverdue = latest.days > section.normalCycleDays;
  const rows = [
    {
      label: t("normalLabel"),
      days: section.normalCycleDays,
      text: t("normalCycle", { days: section.normalCycleDays }),
      overdue: false,
    },
    {
      label: latest.label,
      days: latest.days,
      text: t("currentCycle", { days: latest.days }),
      overdue: isOverdue,
    },
  ];

  return (
    <Rhythm>
      {rows.map((row) => {
        const shareOfLongest = row.days / longest;
        return (
          <Fragment key={row.label}>
            <RhythmLabel as="dt">{row.label}</RhythmLabel>
            <RhythmTrack>
              <RhythmBar
                overdue={row.overdue}
                style={{ "--share-of-longest": shareOfLongest } as CSSProperties}
                aria-hidden="true"
              />
              <RhythmValue overdue={row.overdue}>{row.text}</RhythmValue>
            </RhythmTrack>
          </Fragment>
        );
      })}
    </Rhythm>
  );
}

function OrdersSection({ section, orderListEyebrow }: { section: OrderListSection; orderListEyebrow?: string }) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.orderCard");

  if (!section.items) {
    return <OrderScopeEntries section={section} allOrdersTitle={t("allOrders")} orderListEyebrow={orderListEyebrow} />;
  }
  return <OrderListRows section={section} items={section.items} />;
}

interface OrderListRowsProps {
  section: OrderListSection;
  items: NonNullable<OrderListSection["items"]>;
}

function OrderListRows({ section, items }: OrderListRowsProps) {
  const t = useTranslations("fieldSalesTwo");
  const tCard = useTranslations("fieldSalesTwo.genuiMeetingPrep.orderCard");
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const scope = useOrderScopeLabel(section);
  const drill = useOrderDrill();

  const rows = items.map((order) => ({
    id: order.id,
    date: order.date,
    externalReference: order.externalReference,
    formattedIncomingOrder: order.incomingOrder != null ? formatCurrency(order.incomingOrder, currency, locale) : "—",
    statusLabel: order.invoiceStatus
      ? t(`analysis.orders.invoiceStatus.${INVOICE_STATUS[order.invoiceStatus]}`)
      : undefined,
  }));

  return (
    <OrderList>
      {drill.overlay}
      <MetaLabel>{tCard("ordersCaption", { scope })}</MetaLabel>
      <OrderRows
        items={rows}
        onOpenOrder={(order) => drill.open({ kind: "order", name: order.externalReference, orderId: order.id })}
      />
    </OrderList>
  );
}
